import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import {
  Item,
  ItemContent,
  ItemGroup,
  ItemTitle,
  ItemTitleText,
  ItemTrigger,
} from 'designSystem/components/Item'
import { Text } from 'designSystem/components/Text'
import type { LotoOtherPage } from 'features/lotoReports/lotoIsolationPoints'
import type { ReactElement } from 'react'

type LotoSessionOtherPagesProps = {
  pages: LotoOtherPage[]
  isLoading: boolean
  onOpenPage: (page: LotoOtherPage) => void
}

function OtherPageItem({
  page,
  isLoading,
  onOpenPage,
}: {
  page: LotoOtherPage
  isLoading: boolean
  onOpenPage: (page: LotoOtherPage) => void
}): ReactElement {
  const { t } = useLingui()
  const { documentName, target } = page
  const document = documentName ?? t`Untitled document`
  const pageNumber = target?.pageNumber
  const unavailable = isLoading ? t`Loading page…` : t`Page unavailable`
  const label =
    pageNumber === undefined ? unavailable : t`${document} · page ${pageNumber}`
  return (
    <Item
      role='listitem'
      variant='outline'
      size='xs'
      hoverHighlight={target !== undefined}
      disabled={target === undefined}>
      <ItemTrigger
        disabled={target === undefined}
        onClick={() => onOpenPage(page)}>
        <ItemContent>
          <ItemTitle>
            <ItemTitleText title={label}>{label}</ItemTitleText>
          </ItemTitle>
        </ItemContent>
      </ItemTrigger>
    </Item>
  )
}

export function LotoSessionOtherPages({
  pages,
  isLoading,
  onOpenPage,
}: LotoSessionOtherPagesProps): ReactElement | null {
  if (pages.length === 0) return null
  return (
    <Box display='flex' direction='column' gap='sm'>
      <Text as='h3' variant='caption' color='muted'>
        <Trans>Other pages with no isolation points</Trans>
      </Text>
      <ItemGroup>
        {pages.map((page) => (
          <OtherPageItem
            key={`${page.systemDocumentId}/${page.pageId}`}
            page={page}
            isLoading={isLoading}
            onOpenPage={onOpenPage}
          />
        ))}
      </ItemGroup>
    </Box>
  )
}
